import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { PageHeader, ContentMessage, ActionBar, Spinner } from 'axway-react-ui';
import { Button, Glyphicon } from 'react-bootstrap';
import { Prompt } from 'react-router-dom';
import HTML5Backend from 'react-dnd-html5-backend';
import { DragDropContext } from 'react-dnd';
import { jsonSchemaToJSONPath } from 'components/utility/jsonpath';
import FlowGraphPanel from './FlowGraphPanel';
import FlowToolPanel from './FlowToolPanel';
import InvalidFlowPanel from './InvalidFlowPanel';
import FlowInfoDialog from './FlowInfoDialog';
import FlowPropTypes from './PropTypes';
import ConfigMessage from './ConfigPanel/ConfigMessage';
import NodeConfig from './ConfigPanel/NodeConfig';
import TriggerConfig from './ConfigPanel/TriggerConfig';
import EndpointConfig from './ConfigPanel/EndpointConfig';
import { addTriggerToFlow } from './Graph/FlowGraphUtils';

const triggerErrorTypes = [
	'invalidTriggerParameter',
	'invalidChannelParameter',
	'invalidFlowInputParameter',
	'invalidFlowTriggerPlugin'
];

const invalidParameterTypes = [
	'invalidNodeParameter',
	'invalidTriggerParameter',
	'invalidChannelParameter'
];

const supportedNodeOutputCodes = [
	'NOT_IN_SPEC',
	'INVALID_JSONPATH'
];

class FlowView extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.onNodeSelect = this.onNodeSelect.bind(this);
		this.onSetStartNode = this.onSetStartNode.bind(this);
		this.onFlowDelete = this.onFlowDelete.bind(this);
		this.onNodeConnect = this.onNodeConnect.bind(this);
		this.onNodeDelete = this.onNodeDelete.bind(this);
		this.onNodeInsert = this.onNodeInsert.bind(this);
		this.onNodeInserted = this.onNodeInserted.bind(this);
		this.onTriggerInsert = this.onTriggerInsert.bind(this);
		this.onEdgeSelect = this.onEdgeSelect.bind(this);
		this.onEdgeDelete = this.onEdgeDelete.bind(this);
		this.onFlowSave = this.onFlowSave.bind(this);
		this.onFlowClose = this.onFlowClose.bind(this);
		this.deleteSelectedNode = this.deleteSelectedNode.bind(this);
		this.saveFlow = this.saveFlow.bind(this);
		this.applyFlow = this.applyFlow.bind(this);
		this.deleteFlow = this.deleteFlow.bind(this);
		this.renderDeleteModal = this.renderDeleteModal.bind(this);
		this.onConfigChange = this.onConfigChange.bind(this);
		this.onTriggerConfigParamEnable = this.onTriggerConfigParamEnable.bind(this);
		this.onTriggerConfigChange = this.onTriggerConfigChange.bind(this);
		this.onConfigParamEnable = this.onConfigParamEnable.bind(this);
		this.onConfigParamDelete = this.onConfigParamDelete.bind(this);
		this.onConfigOutputDelete = this.onConfigOutputDelete.bind(this);
		this.renderSaveModal = this.renderSaveModal.bind(this);
		this.renderActions = this.renderActions.bind(this);
		this.getCurrentValidation = this.getCurrentValidation.bind(this);
		this.buildValidationMap = this.buildValidationMap.bind(this);
		this.getSelectors = this.getSelectors.bind(this);
		this.confirmUnload = this.confirmUnload.bind(this);
		this.confirmTransitionMessage = this.confirmTransitionMessage.bind(this);
		this.showEditDetailsModal = this.showEditDetailsModal.bind(this);
		this.renderEditDetailsModal = this.renderEditDetailsModal.bind(this);
		this.onInfoUpdate = this.onInfoUpdate.bind(this);

		this.HANDLED_VALIDATION_ERRORS = [
			'invalidMethod',
			'invalidNodeOutput',
			'invalidStart',
			'invalidNodeParameter',
			'invalidNodeAuthorization',
			'invalidTriggerParameter',
			'invalidChannelParameter',
			'invalidFlowInputParameter',
			'invalidFlowTriggerPlugin'
		];

		this.state = {
			selectedNode: props.selectedNode || null,
			selectedEdge: null,
			flow: props.flow,
			initialFlow: props.flow ? JSON.stringify(props.flow.flow) : null,
			showSaveModal: false,
			showEditDetailsModal: false,
			showCloseModal: false,
			showDeleteModal: false,
			deleteContext: null,
			selectors: props.flow ? this.getSelectors(props.flow) : []
		};

		// internationalize error codes
		this.intl.formatMsg('Invalid value', 'INVALID_VALUE');
		this.intl.formatMsg('Missing value', 'MISSING_VALUE');
		this.intl.formatMsg('Invalid JSONPath', 'INVALID_JSONPATH');
		this.intl.formatMsg('Invalid boolean', 'INVALID_BOOLEAN');
		this.intl.formatMsg('Invalid string', 'INVALID_STRING');
		this.intl.formatMsg('Invalid object', 'INVALID_OBJECT');
		this.intl.formatMsg('Invalid array', 'INVALID_ARRAY');
		this.intl.formatMsg('Invalid number', 'INVALID_NUMBER');
		this.intl.formatMsg('Invalid type', 'INVALID_TYPE');
		this.intl.formatMsg('Unknown credential', 'UNKNOWN_CREDENTIAL');
		this.intl.formatMsg('An update to this flow-node caused this parameter to be renamed or removed, and it is no longer used.', 'PARAM_NOT_IN_SPEC');
		this.intl.formatMsg('An update to this flow-node caused this output to be renamed or removed, and it is no longer used.', 'OUTPUT_NOT_IN_SPEC');
		this.intl.formatMsg('An update to this flow-trigger caused this parameter to be renamed or removed, and it is no longer used.', 'TRIGGER_PARAM_NOT_IN_SPEC');
	}

	isTriggered(flow, flowTriggers) {
		// Check do we have triggers first because we don't have bindings yet
		// (e.g. flowTriggers) for the new flows but we might have unsaved
		// triggers.
		const triggers = flow.flow.triggers;
		for (const type in triggers) {
			if (Object.keys(triggers[type]).length > 0) {
				return true;
			}
		}

		// Check do we have endpoints
		return flowTriggers && flowTriggers.endpoints.length > 0;
	}

	componentDidMount() {
		window.addEventListener('beforeunload', this.confirmUnload);
	}

	componentWillUnmount() {
		window.removeEventListener('beforeunload', this.confirmUnload);
	}

	confirmTransitionMessage() {
		return this.intl.formatMsg('The flow has unsaved changes. Are you sure you want to leave?');
	}

	confirmUnload(event) {
		if (this.isDirty) {
			const msg = this.confirmTransitionMessage();
			// Most browsers are going to ignore this text and use something generic.
			(event || window.event).returnValue = msg;
			return msg;
		}
	}

	// Detects whether or not changes have been applied to the flow or the
	// triggers
	get isDirty() {
		const { flow, initialFlow } = this.state;
		if (!flow || !flow.flow) {
			return false;
		}
		return JSON.stringify(flow.flow) !== initialFlow;
	}

	get isValid() {
		const { flowValidation } = this.props;
		if (flowValidation === undefined) {
			return true;
		}
		return flowValidation
			&& !flowValidation.loading
			&& flowValidation.validation
			&& flowValidation.validation.valid;
	}

	/**
	 * Remove the given parameter from the node's parameter list and update the state with the
	 * change.
	 *
	 * @param  {object} options - The enable options.
	 * @param  {string} options.nodeId  - The name of the node to remove the param from
	 * @param  {string} options.paramId - The name of the param to enable/disable
	 */
	onConfigParamDelete({ nodeId, paramId }) {
		const { flow } = this.state;

		flow.removeParameter(nodeId, paramId);

		this.setState({
			selectors: this.getSelectors(flow)
		}, () => {
			this.props.onChange(flow.flow);
		});
	}

	/**
	 * Enable or disable a parameter (eg. remove/add it to parameters)
	 *
	 * @param  {object} options - The enable options.
	 * @param  {string} options.nodeId  - The name of the node to remove the param from
	 * @param  {string} options.paramId - The name of the param to enable/disable
	 * @param  {boolean} options.enabled - Bool to enable/disable with
	 * @param  {string} options.type - The type of parameter
	 * @param  {string} options.valueType - The input type of that parameter
	 * @param  {string} options.value - The default value for that input type
	 */
	onConfigParamEnable({ nodeId, paramId, enabled, type, valueType, value }) {
		const { flow } = this.state;

		if (!enabled) {
			if (type === 'param') {
				flow.removeParameter(nodeId, paramId);
			} else {
				flow.removeAuthorization(nodeId, paramId);
			}
		}
		// eslint doesn't like an else, so not using it for readability
		if (enabled) {
			// when enabling, create a new parameter using the provided value and type
			if (type === 'param') {
				flow.addParameter(nodeId, paramId, value, valueType);
			} else {
				flow.addAuthorization(nodeId, paramId, value, valueType);
			}
		}
		this.setState({
			selectors: this.getSelectors(flow)
		}, () => {
			this.props.onChange(flow.flow);
		});
	}

	/**
	 * Remove the given output from the node's outputs list and update the state with the change.
	 *
	 * @param  {string} options.nodeId  - The name of the node to remove the param from
	 * @param  {string} options.output - The id of the output
	 */
	onConfigOutputDelete({ nodeId, output }) {
		const { flow } = this.state;
		flow._deleteOutput(nodeId, output);

		this.setState({
			selectors: this.getSelectors(flow)
		}, () => {
			this.props.onChange(flow.flow);
		});
	}

	getSelectors(flow) {
		if (!flow) {
			return [];
		}
		const selectorSet = new Set([ '$' ]);
		if (flow.flow.parameter) {
			const inputs = Object.keys(flow.flow.parameter.properties);

			const paths = [];
			// Build up a list of possible JSON schema paths from the flow input parameter.
			// This is almost always defaulted to: $.params, $.request, $.config, $.env.
			// Below, it adds the additional $.request.body and $.request.headers.
			inputs.forEach((param) => {
				jsonSchemaToJSONPath(
					flow.flow.parameter.properties[param],
					paths,
					`${param}`
				);
			});

			paths.forEach(p => selectorSet.add(p));
		}

		// add flow selectors
		flow.getNodes().forEach((node) => {
			// all parameters
			node.parameters.forEach((param) => {
				if (!param.type || param.type === 'jsonpath') {
					selectorSet.add(param.value);
				}
			});
			// all output contexts
			Object.keys(node.outputs).forEach((output) => {
				const context = node.outputs[output].context;
				if (context) {
					selectorSet.add(context);
				}
			});
		});

		const selectors = Array.from(selectorSet, x => String(x));

		// sort alphabetically
		selectors.sort(function (a, b) {
			return a.localeCompare(b);
		});

		return selectors;
	}

	// TODO(MP): Needs renaming as we have two configs updates now..
	/**
	 * Updates specific parts of the flow in the state.
	 *
	 * @param  {string} type - The flow-node property to be updated.
	 * @param  {string} name - The name of the property we'll update.
	 * @param  {string|object} prop - The destination param value.
	 * @param  {any} newValue - The new value to set.
	 */
	onConfigChange(options) {
		const { type, name, prop, newValue } = options;
		const { flow, selectedNode } = this.state;
		let updateSelectors = false;

		if (type === 'param') {
			flow.updateParameter(selectedNode.id, name, prop, newValue);
			updateSelectors = prop === 'value';
		} else if (type === 'output') {
			flow.updateOutputContext(selectedNode.id, name, newValue);
			updateSelectors = true;
		} else if (type === 'method') {
			const { nodehandlers } = this.props;
			const handlers = nodehandlers && nodehandlers.handlers;
			const node = flow.flow.nodes[selectedNode.id];
			const nodehandler = handlers && handlers[node.type];
			flow.changeMethod(selectedNode.id, newValue, nodehandler);
			updateSelectors = true;
		} else if (type === 'name') {
			flow.changeNodeName(selectedNode.id, newValue);
		} else if (type === 'authz') {
			flow.updateAuthorization(selectedNode.id, name, prop, newValue);
			updateSelectors = prop === 'value';
		}
		const newState = updateSelectors ? {
			selectors: this.getSelectors(flow)
		} : { flow };
		this.setState(newState, () => {
			this.props.onChange(flow.flow);
		});
	}

	onTriggerConfigParamEnable(options) {
		const {
			triggerType,
			triggerId,
			paramId,
			enabled,
			type,
			valueType,
			value
		} = options;
		const { flow } = this.state;
		const { triggers } = flow.flow;
		const trigger = triggers[triggerType][triggerId];

		if (enabled) {
			const parsedValue = (valueType !== 'jsonpath')
				? JSON.parse(value) : value;
			this.onTriggerConfigChange({
				type,
				triggerType,
				triggerId,
				paramId,
				newValue: parsedValue
			});
		} else {
			if (type === 'triggerParameter') {
				delete trigger.triggerParameters[paramId];
			} else if (type === 'inputParameter') {
				delete trigger.flowInputsMap[options.paramId];
			} else { // channelParameter
				delete trigger.channelParameters[paramId];
			}

			this.setState({
				flow
			}, () => {
				this.props.onChange(flow.flow);
			});
		}
	}

	onTriggerConfigChange(options) {
		const { type, triggerType, triggerId, newValue } = options;
		const { flow } = this.state;
		const { triggers } = flow.flow;
		const trigger = triggers[triggerType][triggerId];

		// istanbul ignore else
		if (type === 'name') {
			trigger.instanceName = newValue;
		} else if (type === 'triggerParameter') {
			trigger.triggerParameters[options.paramId] = newValue;
		} else if (type === 'channelParameter') {
			const { channelId } = trigger;
			for (const id in triggers[triggerType]) {
				const target = triggers[triggerType][id];
				// update all of the triggers that share the same channel
				// with the new value.
				if (channelId === target.channelId) {
					target.channelParameters[options.paramId] = newValue;
				}
			}
		} else if (type === 'inputParameter') {
			trigger.flowInputsMap[options.paramId] = newValue;
		}

		this.setState({
			flow
		}, () => {
			this.props.onChange(flow.flow);
		});
	}

	onNodeSelect(selectedNode) {
		if (typeof selectedNode === 'string') {
			selectedNode = {
				id: selectedNode,
				topic: 'parameters',
				type: 'flow-node'
			};
		}
		this.setState({
			selectedNode,
			selectedEdge: null
		});
	}

	onFlowDelete() {
		this.setState({
			showDeleteModal: true,
			deleteContext: 'flow'
		});
	}

	onNodeDelete() {
		this.setState({
			showDeleteModal: true,
			deleteContext: 'node'
		});
	}

	onNodeInsert(node) {
		return this.state.flow.insertNode(node);
	}

	onTriggerInsert(triggerType, triggerId, trigger) {
		addTriggerToFlow(this.state.flow, triggerType, triggerId, trigger);
	}

	onSetStartNode(startNode) {
		if (startNode) {
			this.state.flow.setStart(startNode);
		} else {
			this.state.flow.unsetStart();
		}
		this.props.onChange(this.state.flow.flow);
		if (startNode) {
			this.onNodeSelect(startNode);
		} else {
			this.setState({
				selectedEdge: null
			});
		}
	}

	onNodeConnect(sourceNode, sourceOutput, targetNode) {
		this.state.flow.connectNode(sourceNode, sourceOutput, targetNode);
		this.props.onChange(this.state.flow.flow);
		this.onNodeSelect(targetNode);
	}

	onNodeInserted() {
		// Trigger a change event
		this.setState({
			selectors: this.getSelectors(this.state.flow)
		}, () => {
			this.props.onChange(this.state.flow.flow);
		});
	}

	onEdgeSelect(edge) {
		this.setState({
			selectedNode: null,
			selectedEdge: edge
		});
	}

	onEdgeDelete() {
		this.state.flow.disconnectNode(
			this.state.selectedEdge.from,
			this.state.selectedEdge.output,
			this.state.selectedEdge.to);
		this.props.onChange(this.state.flow.flow);
		this.setState({
			selectedEdge: null
		});
	}

	deleteSelectedNode() {
		const { selectedNode, flow } = this.state;
		if (selectedNode.type === 'trigger') {
			const { type, triggerId } = this.state.selectedNode.options;
			delete flow.flow.triggers[type][triggerId];
		} else {
			flow.deleteNode(this.state.selectedNode.id);
		}
		this.setState({
			flow,
			selectedNode: null,
			showDeleteModal: false,
			deleteContext: null,
			selectors: this.getSelectors(flow)
		}, () => {
			this.props.onChange(this.state.flow.flow);
		});
	}

	deleteFlow() {
		this.setState({
			// update initialFlow so the editor isn't dirty and we can navigate away
			initialFlow: JSON.stringify(this.state.flow.flow),
			showDeleteModal: false,
			deleteContext: null
		}, () => {
			this.props.onDelete();
		});
	}

	onFlowSave() {
		this.setState({
			showSaveModal: true
		});
	}

	onFlowClose() {
		if (!this.isDirty) {
			return this.props.onClose();
		}
		this.setState({
			showCloseModal: true
		});
	}

	saveFlow() {
		const { flow } = this.state;
		this.props.onSave(flow.flow);
	}

	onInfoUpdate(info) {
		this.state.flow.setName(info.name);
		this.state.flow.setDescription(info.description);
		this.props.onChange(this.state.flow.flow);
	}

	renderEditDetailsModal() {
		if (!this.state.flow) {
			return null;
		}
		return (
			<FlowInfoDialog
				show={this.state.showEditDetailsModal}
				onClose={() => {
					this.setState({ showEditDetailsModal: false });
				}}
				onOkay={info => {
					this.setState({ showEditDetailsModal: false });
					this.onInfoUpdate(info);
				}}
				name={this.state.flow.getName()}
				description={this.state.flow.getDescription()}
			/>
		);
	}

	showEditDetailsModal() {
		this.setState({
			showEditDetailsModal: true
		});
	}

	applyFlow() {
		const { flow, selectedNode } = this.state;
		this.props.onApply(flow.flow, selectedNode);

		this.setState({
			showSaveModal: false,
			initialFlow: JSON.stringify(flow.flow)
		});
	}

	/**
	 * Returns the details for the currently selected node.  The details
	 * returned depends on the type of `selectedNode`:
	 *
	 *  * flow-node: `{ id, type, topic, node, nodehandler }`
	 *  * trigger: `{ id, type, topic, trigger }`
	 *  * endpoint: `{ id, type, topic, endpoint }`
	 *
	 * @returns {object} The selected node's details.
	 */
	getSelectionDetail() {
		const { nodehandlers } = this.props;
		const handlers = nodehandlers && nodehandlers.handlers;
		const { selectedNode, flow } = this.state;

		if (!selectedNode || !selectedNode.id) {
			return {};
		}

		const { id, topic, type, options } = selectedNode;

		if (type === 'flow-node') {
			const flowDef = flow.getDefinition();
			const node = flowDef.nodes[id];
			const nodehandler = node && handlers && handlers[node.type];
			return {
				type,
				id,
				topic,
				node,
				nodehandler,
				name: node && node.name
			};
		} else if (type === 'trigger') {
			let trigger;
			if (options && options.triggerId) {
				trigger = flow.flow
					.triggers[options.type][options.triggerId];
			}
			// Triggers have temporary IDs in the UI that are changed while
			// storing them to disk. These IDs can not be used to restore the
			// selection when we apply changes without closing the Flow.  The
			// side effect is that the selection would be lost on apply for
			// the new triggers. If that is the case just return empty
			// selection.
			if (!trigger) {
				return {};
			}
			return {
				type,
				id,
				topic,
				triggerId: options.triggerId,
				triggerType: options.type,
				trigger
			};
		} else if (type === 'endpoint') {
			const endpoint = this.props.flowTriggers.endpoints.find(a => {
				return a.summary === options.summary;
			});
			return {
				type,
				id,
				topic,
				endpoint
			};
		}
		// unknown
		return {
			type
		};
	}

	renderDeleteModal() {
		let summary;
		let name;
		let onAction;

		if (this.state.deleteContext === 'flow') {
			// delete the flow
			name = this.state.flow.getName();
			summary = this.intl.formatMsg('Are you sure you want to delete the flow?', 'flow_delete_msg');
			onAction = this.deleteFlow;
		} else {
			// delete the selection/node
			const selection = this.getSelectionDetail();
			if (!selection.id) {
				return;
			}
			name = (selection.node)
				? selection.node.name
				: selection.trigger.instanceName;

			const isTrigger = !!selection.trigger;
			if (isTrigger && this.props.isTriggeredBySpec) {
				this.setState({
					showDeleteModal: false,
					deleteContext: null
				});
				return;
			}
			summary = isTrigger
				? this.intl.formatMsg('Are you sure you want to delete the flow-trigger?', 'flow_trigger_delete_msg')
				: this.intl.formatMsg('Are you sure you want to delete the flow-node?', 'flow_node_delete_msg');
			onAction = this.deleteSelectedNode;
		}

		return (
			<ContentMessage
				className="lg warning delete"
				title={this.intl.formatMsg('Delete "{name}"', { name })}
				summary={summary}
				actionLabel={this.intl.formatMsg('Proceed')}
				cancelLabel={this.intl.formatMsg('Cancel')}
				onAction={onAction}
				onClose={() => {
					this.setState({
						showDeleteModal: false,
						deleteContext: null
					});
				}}
				imgClass="gg icon-GG-warning"
			/>
		);
	}

	componentWillReceiveProps(nextProps) {
		if (!this.state.flow && nextProps.flow) {
			this.setState({
				initialFlow: JSON.stringify(nextProps.flow.flow),
				flow: nextProps.flow,
				selectors: this.getSelectors(nextProps.flow)
			});
		}
	}

	renderSaveModal() {
		return (
			<ContentMessage
				className="lg info"
				title={this.intl.formatMsg('Save {name}', { name: this.state.flow.getName() })}
				summary={this.intl.formatMsg('Saving the flow will require server restart. Save this flow?')}
				actionLabel={this.intl.formatMsg('Proceed')}
				cancelLabel={this.intl.formatMsg('Cancel')}
				onAction={this.applyFlow}
				onClose={() => {
					this.setState({
						showSaveModal: false
					});
				}}
				imgClass="gg icon-GG-circle-help"
			/>
		);
	}

	renderCloseModal() {
		if (this.isValid) {
			return (
				<ContentMessage
					className="lg info"
					imgClass="gg icon-GG-circle-help"
					title={this.intl.formatMsg('There are unsaved changes')}
					summary={this.intl.formatMsg('Do you wish to save before closing the editor? Saving the flow will require server restart.')}
					actionLabel={this.intl.formatMsg('Save and exit')}
					secondaryActionLabel={this.intl.formatMsg('Discard changes')}
					secondaryActionClass=""
					onAction={this.saveFlow}
					onSecondaryAction={this.props.onClose}
					onClose={() => {
						this.setState({
							showCloseModal: false
						});
					}}
				/>
			);
		} else {
			return (
				<ContentMessage
					className="lg warning"
					imgClass="gg icon-GG-warning"
					title={this.intl.formatMsg('There are unsaved changes')}
					summary={this.intl.formatMsg('The flow is not valid. Do you wish to continue editing or discard changes?')}
					actionLabel={this.intl.formatMsg('Continue editing')}
					secondaryActionLabel={this.intl.formatMsg('Discard changes')}
					secondaryActionClass=""
					onAction={() => {
						this.setState({
							showCloseModal: false
						});
					}}
					onSecondaryAction={this.props.onClose}
					onClose={() => {
						this.setState({
							showCloseModal: false
						});
					}}
				/>
			);
		}
	}

	renderActions() {
		const enabled = this.isDirty && this.isValid;

		return [
			(
				<Button
					key="help"
					bsStyle="link"
					className="flows-doc-link"
					href={window.$conf.docFlowUrl}
					target="_blank"
					title={this.intl.formatMsg('Learn more about - How to manage Flow-nodes')}
				>
					<Glyphicon glyph=" gg icon-GG-share" />
					<span>{this.intl.formatMsg('Help')}</span>
				</Button>
			), (
				<Button
					key="cancel"
					className="cancel"
					bsSize="large"
					bsStyle="link"
					onClick={this.onFlowClose}
				>
					<span>{this.intl.formatMsg('Close')}</span>
				</Button>
			), (
				<Button
					key="save"
					className="save"
					bsSize="large"
					bsStyle="primary"
					onClick={this.onFlowSave}
					disabled={!enabled}
				>
					<Glyphicon glyph=" gg icon-GG-checkmark-circle" />
					<span>{this.intl.formatMsg('Apply')}</span>
				</Button>
			)
		];
	}

	getCurrentValidation(flowGraph) {
		const { flowValidation } = this.props;
		const currentValidation = (flowValidation && flowValidation.validation)
			|| (flowGraph && flowGraph.getValidation());
		return currentValidation;
	}

	/**
	 * Builds a validation map for the UI components for the errors
	 * 	collected.
	 * @param  {object} validation - validation object from @axway/flow
	 * @return {object} - validation object to be easily consumed by UI
	 * {
		valid: false, // Needed to know if flow is valid enough to render
		errors: { // Needed to know if valid at all. eg Object.keys > 0 means invalid
			'<nodeID>': { // Needed to know if specific node is invalid
				methodInvalid: true, // Needed to know if the method is invalid
				paramsInvalid: { // Needed to know if params are invalid
					'param.name': '<code for intl>' // What param is invalid and how
				},
				outputsInvalid: { // Needed to know if outputs are invalid
					outputkey: 'code for intl' // What output is invalid and how
				}
			}
		}
	}
	 */
	buildValidationMap(validation) {
		const validationMap = {
			valid: true,
			nodeErrors: {} // we can nest none-node errors elsewhere in the future
		};
		if (!validation) {
			return;
		}
		if (!validation.valid && validation.errors && validation.errors.length) {
			for (const err of validation.errors) {
				if (err && err.detail) {
					const detail = err.detail;
					const type = err.type;

					// Check if whitelisted error type
					if (!this.HANDLED_VALIDATION_ERRORS.includes(type)) {
						validationMap.valid = false;
						break;
					}

					// The node name in the UI
					let node;
					if (triggerErrorTypes.includes(type)) {
						// The node ID is not passed back from trigger errors,
						// but it passes back `{ type, id }` in `detail` which
						// can be used to construct it.
						node = `$$__axway__trigger_${detail.type}_${detail.id}`;
					} else {
						node = detail.node;
					}

					// Start building nodeErrors object
					validationMap.nodeErrors[node] = validationMap.nodeErrors[node] || {};

					if (type === 'invalidNodeOutput') {
						// if unknown errors are let through and we can't handle them
						// whitelist errorcodes for each type. The UI could get into bad states
						if (!supportedNodeOutputCodes.includes(err.code)) {
							validationMap.valid = false;
							break;
						}
						const oldOutputs = validationMap.nodeErrors[node].outputsInvalid;
						validationMap.nodeErrors[node].outputsInvalid = oldOutputs || {};
						validationMap.nodeErrors[node].outputsInvalid[detail.output] = err.code;
					}

					// Check if node has an invalid method
					if (type === 'invalidMethod') {
						validationMap.nodeErrors[node].methodInvalid = true;
					}

					// Check if node has invalid node|trigger|channel parameter
					if (invalidParameterTypes.includes(type)) {
						// Currently we cannot fix this in the UI for channels,
						// so we decided to display the FLOW error page and
						// let the user figure it out from the logs.
						if (type === 'invalidChannelParameter'
							&& err.code === 'NOT_IN_SPEC') {
							validationMap.valid = false;
							continue;
						}

						const oldParams = validationMap.nodeErrors[node]
							.paramsInvalid;
						validationMap.nodeErrors[node]
							.paramsInvalid = oldParams || {};

						// we'll just keep one error per param for ui sake,
						// fixing one will bring up the next
						validationMap.nodeErrors[node]
							.paramsInvalid[detail.parameter] = err.code;
					}

					// Check if node has invalid flow input parameter
					if (type === 'invalidFlowInputParameter') {
						// Currently we cannot fix this in the UI easily,
						// so we decided to display the FLOW error page and
						// let the user figure it out from the logs.
						if (err.code === 'NOT_IN_SPEC') {
							validationMap.valid = false;
							break;
						}

						const oldParams = validationMap.nodeErrors[node]
							.inputParamsInvalid;
						validationMap.nodeErrors[node]
							.inputParamsInvalid = oldParams || {};

						// we'll just keep one error per param for ui sake,
						// fixing one will bring  up the next
						validationMap.nodeErrors[node]
							.inputParamsInvalid[detail.parameter] = err.code;
					}

					// Check if node has invalid authorization
					if (type === 'invalidNodeAuthorization') {
						if (err.code === 'NOT_IN_SPEC') {
							validationMap.valid = false;
							break;
						}
						const oldParams = validationMap.nodeErrors[node].authorizationInvalid;
						validationMap.nodeErrors[node].authorizationInvalid = oldParams || {};

						// we'll just keep one error per param for ui sake, fixing one will bring
						// up the next
						validationMap.nodeErrors[node].authorizationInvalid[detail.parameter]
							= err.code;
					}
				} else if (err && !err.detail && err.type === 'invalidStart') {
					// If invalid start then mark it as invalid
					const node = '$$__axway__start';
					validationMap.nodeErrors[node] = validationMap.nodeErrors[node] || {};
				}
			}
		} else if (!validation.valid) {
			// Otherwise we don't display flow
			validationMap.valid = false;
		}

		return validationMap;
	}

	shouldComponentUpdate(nextProps) {
		if ((nextProps.flowValidation && nextProps.flowValidation.loading)
			|| (nextProps.nodehandlers && nextProps.nodehandlers.loading)) {
			return false;
		}
		return true;
	}

	render() {
		// This message denotes a new trigger. We are using it to differentiate
		// between new triggers and existing ones, so we can hide the Trigger ID
		// in the UI(for the new triggers) or show it(for existing ones).
		const newTriggerStatusMsg
			= this.intl.formatMsg('This flow-trigger is new and has not been saved yet.');
		const {
			nodehandlers,
			children,
			isNewFlow,
			credentials,
			debug,
			startDebug,
			clearDebug,
			flowId,
			projectName,
			flowTriggers,
			triggerDefinitions
		} = this.props;
		const { selectors } = this.state;
		const handlers = nodehandlers && nodehandlers.handlers;
		const { flow } = this.state;

		const selection = this.getSelectionDetail();
		const panels = [];
		const validation = this.getCurrentValidation(flow);
		const validationMap = this.buildValidationMap(validation);
		const hasNoErrors = !!validation && !!validation.valid;
		const canDebug = !!hasNoErrors;
		const isEditableFlow
			= ((validationMap && validationMap.valid) || isNewFlow) && !!flow;

		if (flow && handlers && triggerDefinitions) {
			panels.push(<FlowToolPanel
				key="toolpanel"
				nodehandlers={handlers}
				triggerDefinitions={triggerDefinitions}
				draggable={!!isEditableFlow}
				showFlowTriggers={!this.props.isTriggeredBySpec}
				filterFlowTriggers={this.props.specFlowTriggerTypeIds}
			/>);
		}

		if (isEditableFlow && handlers && triggerDefinitions) {
			const isTriggered = this.isTriggered(flow, flowTriggers);
			panels.push(
				<FlowGraphPanel
					key="graphpanel"
					nodehandlers={handlers}
					flow={flow}
					flowTriggers={flowTriggers}
					triggerDefinitions={triggerDefinitions}
					triggered={isTriggered}
					newTriggerStatusMsg={newTriggerStatusMsg}
					flowId={flowId}
					projectName={projectName}
					canDebug={canDebug}
					debug={debug}
					startDebug={startDebug}
					clearDebug={clearDebug}
					selectedNode={this.state.selectedNode}
					selectedEdge={this.state.selectedEdge}
					onNodeSelect={this.onNodeSelect}
					onSetStartNode={this.onSetStartNode}
					onNodeConnect={this.onNodeConnect}
					onNodeDelete={this.onNodeDelete}
					onNodeInsert={this.onNodeInsert}
					onNodeInserted={this.onNodeInserted}
					onTriggerInsert={this.onTriggerInsert}
					validationMap={validationMap}
					onEdgeSelect={this.onEdgeSelect}
					onEdgeDelete={this.onEdgeDelete}
				/>
			);
			let configPanel;
			if (selection.type === 'flow-node') {
				configPanel = (
					<NodeConfig
						// See: https://kentcdodds.com/blog/understanding-reacts-key-prop
						// The importance is that when a key stays the same, it's
						// children are re-rendered when the props change, but the
						// "instance" of the components in the tree (with the same keys)
						// stay the same. Some components with internal state (such as
						// Typeahead) don't update when the props are changed since they
						// may only use props for the intial render or populating the
						// state. This means that only a full re-render will allow us
						// to control the prop(s) from the outside the component.
						// In this case, by changing the key we are forcing all child
						// elements to be re-created from their constructors, ensuring
						// that the initial values we provide are respected.
						key={`config_${selection.type}_${selection.id}`}
						id={selection.id}
						node={selection.node}
						nodehandler={selection.nodehandler}
						topic={selection.topic}
						schemas={this.props.schemas}
						validationMap={validationMap}
						selectors={selectors}
						credentials={credentials}
						onParamEnable={this.onConfigParamEnable}
						onParamDelete={this.onConfigParamDelete}
						onOutputDelete={this.onConfigOutputDelete}
						onConfigChange={this.onConfigChange}
						onConfigTopicChange={this.onNodeSelect}
					/>
				);
			} else if (selection.type === 'trigger') {
				// For trigger/channel parameters, these are the only
				// selectors possible.
				const triggerSelectors = [
					'$.config',
					'$.env'
				];
				const definition
					= triggerDefinitions[selection.triggerType];

				configPanel = (
					<TriggerConfig
						// Unique key ensures the tree fully re-renders when the
						// selection changes. See note above.
						key={`config_trigger_${selection.triggerType}_${selection.triggerId}`}
						id={selection.id}
						triggerId={selection.triggerId}
						triggerType={selection.triggerType}
						trigger={selection.trigger}
						newTriggerStatusMsg={newTriggerStatusMsg}
						definition={definition}
						flowParameter={flow.flow.parameter}
						topic={selection.topic}
						validationMap={validationMap}
						selectors={triggerSelectors}
						onParamEnable={this.onTriggerConfigParamEnable}
						onChange={this.onTriggerConfigChange}
						onTabSelect={(topic, ev) => {
							ev.stopPropagation();
							// id, topic, type, options
							this.onNodeSelect({
								id: selection.id,
								topic,
								type: 'trigger',
								options: {
									triggerId: selection.triggerId,
									type: selection.triggerType
								}
							});
						}}
					/>
				);
			} else if (selection.type === 'endpoint') {
				configPanel = (
					<EndpointConfig
						// This will need a unique key if we add uncontrolled inputs to
						// the component. We don't have enough info right now to generate
						// that key. (It's possible that selection.endpoint.summary is
						// enough, but we can't guarantee that it is definitley unique.)
						key="endpointpanel"
						endpoint={selection.endpoint}
					/>
				);
			} else if (selection.type === 'start' && !isTriggered) {
				configPanel = (
					<ConfigMessage
						key="emptypanel"
						variant="warning"
						title={this.intl.formatMsg('Flow is not triggered')}
						message={this.intl.formatMsg('Currently, this flow can only be invoked by the flow debugger. However, it can be configured to be integrated with a plugin capable of triggering this flow.')}
						linkText={this.intl.formatMsg('Refer to flow-triggers documentation for more information')}
						linkURL={window.$conf.docTriggerUrl}
					/>
				);
			} else {
				configPanel = (
					<ConfigMessage
						key="emptypanel"
						variant="info"
						title={this.intl.formatMsg('Select a node to configure its properties')}
					/>
				);
			}
			panels.push(
				<div className="flow-config-panel" key="configpanel">
					<div className="scroll-container">
						{configPanel}
					</div>
				</div>
			);
		} else if (((flow && !isEditableFlow) || !flow) && nodehandlers.loading !== true) {
			panels.push(
				<InvalidFlowPanel
					key="invalidpanel"
					action={() => {
						window.open(window.$conf.docFlowUrl, '_blank');
					}}
				/>
			);
		} else {
			panels.push(
				<div key="spinner" className="loading-panel">
					<Spinner bsSize="large" />
				</div>
			);
		}

		const actions = [
			<ActionBar.Button
				key="edit-button"
				tooltip={this.intl.formatMsg('Edit flow details')}
				icon="pencil"
				onClick={this.showEditDetailsModal}
				bsStyle="link"
			/>
		];
		if (!isNewFlow && !flowTriggers.endpoints.length) {
			actions.push(
				<ActionBar.Button
					key="delete-button"
					tooltip={this.intl.formatMsg('Delete flow')}
					icon="trash2"
					onClick={this.onFlowDelete}
					bsStyle="link"
				/>
			);
		}

		return (
			<div className="flow-view">
				<Prompt
					when={this.isDirty && !this.state.showCloseModal}
					message={this.confirmTransitionMessage()}
				/>
				{this.state.showDeleteModal && this.renderDeleteModal()}
				{this.state.showSaveModal && this.renderSaveModal()}
				{this.state.showCloseModal && this.renderCloseModal()}
				{this.renderEditDetailsModal()}
				<PageHeader>
					{flow && (
						<PageHeader.Section align="left" compact className="flow-left-actions">
							<PageHeader.Title>
								{flow.getName()}
							</PageHeader.Title>
							<ActionBar>
								{actions}
							</ActionBar>
						</PageHeader.Section>
					)}
					<PageHeader.Section align="right">
						{this.renderActions()}
					</PageHeader.Section>
				</PageHeader>
				<div className="workarea">
					{panels.length ? panels : children}
				</div>
			</div>
		);
	}
}

FlowView.defaultProps = {
	isNewFlow: false,
	flowTriggers: {
		endpoints: [],
		triggers: {}
	}
};

FlowView.propTypes = {
	/**
	 * The flow definition.
	 */
	flow: PropTypes.object,
	/**
	 * The flow-trigger bindings to the flow.
	 */
	flowTriggers: FlowPropTypes.flowTriggers,
	/**
	 * The flow-trigger definitions
	 */
	triggerDefinitions: FlowPropTypes.triggerDefinitions,
	/**
	 * A map of the node handlers.
	 */
	nodehandlers: PropTypes.object,
	/**
	 * A map of the schemas.
	 */
	schemas: PropTypes.object,
	/**
	 * A flow validation status object.
	 */
	flowValidation: PropTypes.shape({
		loading: PropTypes.bool,
		error: PropTypes.bool,
		validation: PropTypes.object
	}),
	/**
	 * A flow saving status object.
	 */
	flowSaving: PropTypes.shape({
		saving: PropTypes.bool,
		saveError: PropTypes.bool,
		saveErrorDetail: PropTypes.object
	}),
	/**
	 * Close event
	 */
	onClose: PropTypes.func.isRequired,
	/**
	 * Change event
	 */
	onChange: PropTypes.func.isRequired,
	/**
	 * Save event
	 */
	onSave: PropTypes.func.isRequired,
	/**
	 * Apply event
	 */
	onApply: PropTypes.func.isRequired,
	/**
	 * Delete flow event
	 */
	onDelete: PropTypes.func.isRequired,
	/**
	 * Flag indicating if its a new flow. Default is false.
	 */
	isNewFlow: PropTypes.bool,
	/**
	 * Flag to indicate that the flow is triggered by a spec.
	 */
	isTriggeredBySpec: PropTypes.bool,
	/**
	 * A list of flowTriggerTypeId that are from specs.
	 */
	specFlowTriggerTypeIds: PropTypes.arrayOf(PropTypes.string),
	/*
	 * The available credentials that can be set on a node
	 */
	credentials: PropTypes.object
};

export default DragDropContext(HTML5Backend)(injectIntl(FlowView));
